import type { CarePlan } from '@medplum/fhirtypes';
import { AnimatePresence, motion } from 'framer-motion';

import { Button, Icon, LoadingIcon, Text } from '@eluve/components';
import { PLAN_SHAPES } from '@eluve/plan-shapes';

import { carePlanSummary } from './care-plan-meta';
import type { CarePlanDraftState } from './useAppointmentCarePlanDraft';

const WRAPPER_CLASS =
  'flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3.5 shadow-sm';

const Wrapper: React.FC<{
  className: string;
  children: React.ReactNode;
}> = ({ className, children }) => (
  <AnimatePresence>
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className={`${WRAPPER_CLASS} ${className}`}
    >
      {children}
    </motion.div>
  </AnimatePresence>
);

const DismissButton: React.FC<{ onDismiss: () => void }> = ({ onDismiss }) => (
  <Button variant="ghost" size="sm" aria-label="Dismiss" onClick={onDismiss}>
    <Icon name="X" size="auto" />
  </Button>
);

// The animated banner above the active tab content that tracks the
// end-of-encounter Care Plan job (ENG-4699/ENG-4706). One component, four
// derived states: `generating` ("preparing…"), `ready` (the drafted plan with a
// Review CTA), `none` (the matcher quiet-skipped), and `failed` (the job threw).
// Dismiss is session-local (the parent drops it from state).
export const CarePlanCallout: React.FC<{
  state: CarePlanDraftState;
  carePlan: CarePlan | null;
  onReview: () => void;
  onDismiss: () => void;
}> = ({ state, carePlan, onReview, onDismiss }) => {
  if (state === 'ready' && carePlan) {
    const { title, shape, phaseCount, weeks, matchPct } =
      carePlanSummary(carePlan);
    // Phase count/weeks only describe a phased plan; for verbatim
    // (continuous/episodic) drafts show just the shape label (ENG-4773).
    const isPhased = !shape || shape === 'phased';
    const meta = [
      shape ? PLAN_SHAPES[shape].label : 'Phased',
      isPhased ? `${phaseCount} phase${phaseCount === 1 ? '' : 's'}` : null,
      isPhased && weeks ? `${weeks} weeks` : null,
      matchPct !== null ? `${matchPct}% match` : null,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <Wrapper className="border-border-indigo-subtle bg-linear-to-r from-bg-indigo-subtle-secondary to-transparent">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-bg-indigo-strong-primary text-text-indigo-strong">
          <Icon name="ClipboardCheck" size="auto" className="size-4" />
        </span>
        <Text size="sm" className="min-w-0 text-foreground">
          Eluve drafted a <span className="font-semibold">Care Plan</span> for
          this visit — <span className="font-semibold">{title}</span>
          <span className="text-muted-foreground"> · {meta}</span>
        </Text>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="brand" size="sm" onClick={onReview}>
            Review &amp; tailor
            <Icon name="ArrowRight" size="auto" />
          </Button>
          <DismissButton onDismiss={onDismiss} />
        </div>
      </Wrapper>
    );
  }

  if (state === 'generating') {
    return (
      <Wrapper className="border-border-indigo-subtle bg-linear-to-r from-bg-indigo-subtle-secondary to-transparent">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-bg-indigo-strong-primary text-text-indigo-strong">
          <LoadingIcon size="sm" />
        </span>
        <Text size="sm" className="min-w-0 text-foreground">
          Eluve is preparing a <span className="font-semibold">Care Plan</span>{' '}
          for this visit…
        </Text>
      </Wrapper>
    );
  }

  if (state === 'none') {
    return (
      <Wrapper className="border-border-primary bg-bg-secondary">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-bg-tertiary text-text-tertiary">
          <Icon name="Info" size="auto" className="size-4" />
        </span>
        <Text size="sm" color="secondary" className="min-w-0">
          No care plan was recommended for this visit.
        </Text>
        <div className="ml-auto flex items-center gap-1">
          <DismissButton onDismiss={onDismiss} />
        </div>
      </Wrapper>
    );
  }

  if (state === 'failed') {
    return (
      <Wrapper className="border-border-warning-subtle bg-bg-warning-subtle">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg text-text-warning">
          <Icon name="AlertTriangle" size="auto" className="size-4" />
        </span>
        <Text size="sm" className="min-w-0 text-text-warning">
          Eluve couldn&apos;t prepare a care plan for this visit.
        </Text>
        <div className="ml-auto flex items-center gap-1">
          <DismissButton onDismiss={onDismiss} />
        </div>
      </Wrapper>
    );
  }

  return null;
};
